/**
 * Browser Supabase client. Uses only the public anon key (safe to ship — RLS
 * protects the data). Staff read/write goes directly through this client; the
 * JWT it holds is also sent to the backend for secret operations.
 */
import { createClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabaseConfigured = !!url && !!anon;
if (!supabaseConfigured) {
  // Surface a clear message in dev rather than failing cryptically later.
  console.warn('[supabase] VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are not set. See .env.example.');
}

// Fall back to a URL-shaped placeholder so createClient never throws and the app
// still renders (staff data calls will simply fail until real config is provided).
export const supabase = createClient(url || 'https://placeholder.supabase.co', anon || 'placeholder', {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

/** Start the Microsoft Entra (Azure) OAuth sign-in flow. */
/** Where to land after OAuth returns. Survives the round-trip to Microsoft. */
const RETURN_KEY = 'postLoginPath';
const FRESH_KEY = 'postLoginFresh';

/**
 * The destination for a sign-in that just completed, or null if this is simply a
 * page load with an existing session.
 *
 * The distinction matters: without it, a staff member who refreshes while on the
 * queue would get thrown to the hub every time. Only a genuine sign-in relocates
 * anyone.
 */
export function takePostLoginPath(): string | null {
  if (!sessionStorage.getItem(FRESH_KEY)) return null;
  sessionStorage.removeItem(FRESH_KEY);
  const p = sessionStorage.getItem(RETURN_KEY);
  sessionStorage.removeItem(RETURN_KEY);
  // Signing in from the front door lands on the hub — it is the way in to
  // everything now. Signing in from somewhere specific returns you there.
  return p || '/hub';
}

export async function signInWithMicrosoft(): Promise<void> {
  // Remember where the user actually was. `redirectTo` stays on the bare origin
  // because Supabase only accepts redirect URLs on its configured allow-list, and
  // widening that list would touch live COMM-PASS sign-in. Storing the path here
  // and restoring it on return does the same job with no config change.
  const here = window.location.pathname + window.location.search;
  sessionStorage.setItem(FRESH_KEY, '1');
  if (here && here !== '/') sessionStorage.setItem(RETURN_KEY, here);

  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'azure',
    options: {
      scopes: 'email openid profile',
      redirectTo: window.location.origin,
    },
  });
  if (error) throw error;
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
}
